import { useState } from "react";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Download, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "../i18n";

export default function Downloads() {
  const { t } = useTranslation();
  const [orderId, setOrderId] = useState("");
  const [submittedOrderId, setSubmittedOrderId] = useState<number | null>(null);

  const downloadUrls = trpc.downloads.getAllDownloadUrls.useQuery(
    { orderId: submittedOrderId! },
    {
      enabled: submittedOrderId !== null,
      retry: false,
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const id = parseInt(orderId, 10);
    if (isNaN(id)) {
      toast.error(t.downloads.invalidOrderNumber);
      return;
    }
    setSubmittedOrderId(id);
  };

  const handleDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`${t.downloads.downloadStarted} ${filename}`);
    } catch (error) {
      toast.error(`${t.downloads.downloadError} ${filename}`);
    }
  };

  const handleDownloadAll = async () => {
    if (!downloadUrls.data?.urls) return;

    toast.info(`${t.common.download} ${downloadUrls.data.urls.length} ${t.images.imageCount}...`);

    for (const item of downloadUrls.data.urls) {
      if (item) {
        await handleDownload(item.url, item.filename);
        // Small delay between downloads to avoid overwhelming the browser
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    toast.success(t.downloads.allDownloaded);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="container max-w-4xl">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold mb-2">{t.downloads.title}</h1>
          <p className="text-muted-foreground">
            {t.downloads.enterOrderId}
          </p>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t.downloads.orderIdLabel}</CardTitle>
            <CardDescription>
              {t.checkout.orderConfirmation}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="flex gap-4">
              <Input
                type="number"
                placeholder={t.downloads.orderIdPlaceholder}
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                className="flex-1"
                required
              />
              <Button type="submit" disabled={downloadUrls.isLoading}>
                {downloadUrls.isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t.common.loading}
                  </>
                ) : (
                  t.downloads.submit
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {downloadUrls.isError && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <div>
                  <p className="font-medium">{t.downloads.orderNotFound}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t.orders.imagesAvailableAfterPayment}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {downloadUrls.isSuccess && downloadUrls.data.urls.length > 0 && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      {t.images.title}
                    </CardTitle>
                    <CardDescription>
                      {downloadUrls.data.urls.length} {t.images.imageCount}
                    </CardDescription>
                  </div>
                  <Button onClick={handleDownloadAll}>
                    <Download className="mr-2 h-4 w-4" />
                    {t.downloads.downloadAll}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3">
                  {downloadUrls.data.urls.map((item) => {
                    if (!item) return null;
                    return (
                      <div
                        key={item.imageId}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded bg-primary/10 flex items-center justify-center">
                            <Download className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{item.filename}</p>
                            <p className="text-sm text-muted-foreground">
                              {t.images.original}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleDownload(item.url, item.filename)}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          {t.downloads.download}
                        </Button>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <strong>Napomena:</strong> Linkovi za preuzimanje su važeći {Math.floor(downloadUrls.data.expiresIn / 60)} minuta.
                    Nakon isteka, osvježite stranicu da dobijete nove linkove.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {downloadUrls.isSuccess && downloadUrls.data.urls.length === 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-muted-foreground">
                <AlertCircle className="h-5 w-5" />
                <p>{t.downloads.noImages}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
